import { getSelectItemFromModelName } from '@/components/data-table/custom-tables/select-table/select-items/relation-item-map';
import { CommandGroup, CommandItem } from '@/components/ui/command';
import { cn } from '@/lib/cn';
import { useRouter } from 'next/navigation';
import { useQueries } from '@tanstack/react-query';
import { get } from '@/requests/api/get';
import { ModelNameUrlMap } from '@/lib/urls';
import { getConfig } from '@/lib/get-config';
import { Skeleton } from '@/components/ui/skeleton';
import { useRecentSearches } from '../hooks/use-recent-searches';
import type { ModelName } from '@pigello/pigello-matrix';

export function RecentSearches({ onClose }: { onClose: () => void }) {
  const { recentSearches, clearRecentSearches, placeRecentSearchAtTop } =
    useRecentSearches();
  const router = useRouter();
  const recentSearchesQueries = useQueries({
    queries: recentSearches.map(({ id, modelName }) => ({
      queryKey: [modelName, id, undefined],
      queryFn: async () => {
        const instance = await get({ modelName, id });
        return { ...instance };
      },
    })),
    combine: (results) => {
      return {
        data: results
          .map((result) => result.data)
          .filter((result) => result !== undefined),
        isLoading: results.some((result) => result.isLoading),
        isFetching: results.some((result) => result.isFetching),
        isSuccess: results.every((result) => result.isSuccess),
        isError: results.some((result) => result.isError),
      };
    },
  });

  const modelNameMap = new Map<string, ModelName>(
    recentSearches.map(({ id, modelName }) => [id, modelName])
  );

  if (!recentSearches?.length) {
    return null;
  }

  if (recentSearchesQueries?.isLoading) {
    return (
      <CommandGroup
        heading={
          <div className='flex items-center justify-between gap-2'>
            <span>Senast sökt</span>
            <button
              className='transition-colors hover:text-muted-foreground/70'
              onClick={() => clearRecentSearches()}
            >
              Rensa senast sökt
            </button>
          </div>
        }
      >
        {recentSearches.map((_search, index) => (
          <CommandItem key={`skeleton-${index}`} disabled>
            <div className='flex w-full'>
              <div className='mr-4'>
                <Skeleton className='h-8 w-8 rounded-full' />
              </div>
              <div className='w-full space-y-2'>
                <div className='flex w-full items-center justify-between gap-2'>
                  <Skeleton className='h-4 w-[60%]' />
                  <Skeleton className='h-5 w-16 rounded-full' />
                </div>
                <Skeleton className='h-3 w-[80%]' />
              </div>
            </div>
          </CommandItem>
        ))}
      </CommandGroup>
    );
  }

  return (
    <CommandGroup
      heading={
        <div className='flex items-center justify-between gap-2'>
          <span>Senast sökt</span>
          <button
            type='button'
            className='transition-colors hover:text-muted-foreground/70'
            onClick={() => clearRecentSearches()}
          >
            Rensa senast sökt
          </button>
        </div>
      }
    >
      {recentSearchesQueries?.data.map((instance) => {
        const modelName = modelNameMap.get(instance.id);
        if (!modelName) {
          return null;
        }
        const config = getConfig(modelName);
        const verboseName = config?.verboseName;
        const selectItem = getSelectItemFromModelName(modelName);
        const rendered = selectItem.renderItem(instance);
        const renderedWithDescription = selectItem.renderItem(instance, true);
        return (
          <CommandItem
            key={instance.id}
            value={instance.id}
            keywords={[instance.id]}
            onSelect={() => {
              // Place the chosen search at the top of the list
              placeRecentSearchAtTop(instance.id, modelName);
              const url = ModelNameUrlMap?.[modelName] ?? undefined;
              router.push(url?.(instance.id) ?? '/not-found');
              onClose();
            }}
          >
            <div className='flex w-full'>
              <div className={cn('mr-4', !rendered.avatar && 'hidden')}>
                {rendered.avatar}
              </div>
              <div className='w-full'>
                <div className='flex w-full items-center justify-between'>
                  <span>
                    <span className='font-medium'>{verboseName}</span>{' '}
                    {rendered.title}
                  </span>{' '}
                  <span>{rendered.badge}</span>
                </div>
                <div className='text-xs whitespace-nowrap text-muted-foreground'>
                  {renderedWithDescription.descriptionItems}
                </div>
              </div>
            </div>
          </CommandItem>
        );
      })}
    </CommandGroup>
  );
}
